import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import bg from "../assets/background.png";
import Footer from "../components/footer";
import { usePages, useSchedule, groupsForPage } from "../data/useLocations";
import Seo from "../components/Seo";

const MYMAPS_EMBED_URL =
  "https://www.google.com/maps/d/u/1/embed?mid=1T7Kvr_lvmlA2jBmagNZycordFNcYCCk&ehbc=2E312F&noprof=1";

const INFO_CARDS = [
  { label: "Dojazd", text: "Kliknij pinezkę i wyznacz trasę do celu!" },
  { label: "Masz blisko?", text: "Wejdź w lokalizację i sprawdź grafik!" },
  { label: "Masz pytanie?", text: 'Zostaw kontakt do siebie w zakładce "Kontakt"!' },
];

/** "2 sale", "5 sal" */
function roomsLabel(count) {
  return count >= 2 && count <= 4 ? `${count} sale` : `${count} sal`;
}

export default function LocationsPage() {
  const { pages, regions, loading, error } = usePages();
  const { data } = useSchedule();

  const groups = data?.groups || [];

  const [active, setActive] = useState("all");

  /** Etykieta obszaru po id — z regions[] w public_site/schedule. */
  const areaLabel = useMemo(() => {
    const map = new Map();
    regions.forEach((r) => map.set(r.id, r.label));
    return map;
  }, [regions]);

  /** Tylko te obszary, w których faktycznie są placówki. */
  const filters = useMemo(() => {
    const present = new Set(pages.map((p) => p.area).filter(Boolean));
    return [
      { id: "all", label: "Wszystkie" },
      ...regions.filter((r) => present.has(r.id)),
    ];
  }, [pages, regions]);

  const filtered = useMemo(() => {
    if (active === "all") return pages;
    return pages.filter((p) => p.area === active);
  }, [active, pages]);

  /** Placówka bez zajęć w żadnej sali → grafik jeszcze w przygotowaniu. */
  const hasSchedule = useMemo(() => {
    const map = new Map();
    pages.forEach((p) => {
      map.set(p.slug, groupsForPage(groups, p.roomIds).length > 0);
    });
    return map;
  }, [pages, groups]);

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        background: `#ededed url(${bg}) center / cover no-repeat`,
        paddingTop: "80px",
      }}
    >
      <Seo
        title="Nasze lokalizacje - Wrocław, Jastrzębie-Zdrój, Mszana i okolice"
        description="22 placówki 4STARS. Znajdź najbliższą szkołę tańca i akrobatyki powietrznej dla dziecka - sprawdź adresy, grafik zajęć i cennik."
        path="/lokalizacje"
      />

      <section
        style={{
          width: "92%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          margin: "10px auto 10px",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "34px", fontWeight: 900, color: "rgba(0,0,0,0.92)" }}>
          Nasze lokalizacje
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "13px",
            fontWeight: 700,
            color: "rgba(0,0,0,0.62)",
            maxWidth: "820px",
          }}
        >
          Wybierz placówkę najbliżej Ciebie. Na mapie znajdziesz wszystkie nasze placówki - kliknij
          pinezkę i od razu poznaj trasę dojazdu!
        </p>
      </section>

      {/* MAPA */}
      <section
        style={{
          width: "92%",
          maxWidth: "1200px",
          margin: "14px auto 26px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "520px",
            borderRadius: "22px",
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.92)",
          }}
        >
          <iframe
            title="Mapa lokalizacji 4STARS"
            src={MYMAPS_EMBED_URL}
            width="100%"
            height="100%"
            style={{ border: "0px" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {INFO_CARDS.map((c) => (
            <div
              key={c.label}
              style={{
                flex: "1 1 260px",
                backgroundColor: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "18px",
                padding: "14px 16px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 900,
                  color: "rgba(0,0,0,0.60)",
                  marginBottom: "5px",
                }}
              >
                {c.label}
              </div>
              <div style={{ fontSize: "14px", fontWeight: 900, color: "rgba(0,0,0,0.92)" }}>
                {c.text}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FILTRY */}
      {filters.length > 1 ? (
        <section
          style={{
            width: "92%",
            maxWidth: "1200px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: "14px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              gap: "10px",
              paddingBottom: "6px",
              flexWrap: "wrap",
            }}
          >
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setActive(f.id)}
                style={{
                  flex: "0 0 auto",
                  border: active === f.id ? "1px solid #d0142d" : "1px solid rgba(0,0,0,0.12)",
                  backgroundColor: active === f.id ? "#d0142d" : "rgba(255,255,255,0.95)",
                  color: active === f.id ? "rgb(250,250,250)" : "rgba(0,0,0,0.85)",
                  borderRadius: "999px",
                  padding: "9px 14px",
                  fontSize: "12px",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* LISTA */}
      <section
        style={{
          width: "92%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          paddingBottom: "60px",
        }}
      >
        {loading ? <ListSkeleton /> : null}

        {!loading && error ? (
          <EmptyBox text="Nie udało się wczytać listy placówek. Odśwież stronę lub spróbuj za chwilę." />
        ) : null}

        {!loading && !error && filtered.length === 0 ? (
          <EmptyBox text="Brak placówek w tej kategorii." />
        ) : null}

        {filtered.map((p) => {
          const color = p.color || "#d0142d";
          const soon = !hasSchedule.get(p.slug);
          const label = areaLabel.get(p.area) || "";

          return (
            <Link
              key={p.slug}
              to={`/${p.slug}`}
              style={{ textDecoration: "none", width: "100%" }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "rgba(255,255,255,0.92)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderLeft: `8px solid ${color}`,
                  borderRadius: "18px",
                  padding: "14px 16px",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontSize: "14px", fontWeight: 900, color: "#111" }}>
                      {p.name}
                    </span>

                    {label ? (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 900,
                          color: color,
                          backgroundColor: `${color}1a`,
                          borderRadius: "999px",
                          padding: "4px 10px",
                        }}
                      >
                        {label}
                      </span>
                    ) : null}

                    {p.isMulti ? (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 900,
                          color: "rgba(0,0,0,0.55)",
                          backgroundColor: "rgba(0,0,0,0.06)",
                          borderRadius: "999px",
                          padding: "4px 10px",
                        }}
                      >
                        {roomsLabel(p.rooms.length)}
                      </span>
                    ) : null}

                    {soon ? (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 900,
                          color: "rgba(0,0,0,0.55)",
                          backgroundColor: "rgba(0,0,0,0.06)",
                          borderRadius: "999px",
                          padding: "4px 10px",
                        }}
                      >
                        Grafik wkrótce
                      </span>
                    ) : null}
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 800,
                      color: "rgba(0,0,0,0.62)",
                      marginTop: "-4px",
                    }}
                  >
                    {p.address}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 900,
                    color: "rgb(250,250,250)",
                    borderRadius: "999px",
                    padding: "10px 12px",
                    backgroundColor: color,
                    flex: "0 0 auto",
                  }}
                >
                  Sprawdź
                </div>
              </div>
            </Link>
          );
        })}
      </section>

      <Footer />
    </div>
  );
}

function ListSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Wczytywanie placówek"
      style={{ display: "flex", flexDirection: "column", gap: "12px" }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            display: "block",
            width: "100%",
            height: "74px",
            borderRadius: "18px",
            backgroundColor: "rgba(255,255,255,0.55)",
            border: "1px solid rgba(0,0,0,0.05)",
          }}
        />
      ))}
    </div>
  );
}

function EmptyBox({ text }) {
  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: "18px",
        padding: "22px 16px",
        textAlign: "center",
        fontSize: "13px",
        fontWeight: 700,
        color: "rgba(0,0,0,0.6)",
      }}
    >
      {text}
    </div>
  );
}